import { Component, OnInit, OnDestroy, ChangeDetectorRef, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Event, EventsService, Subevento, FechasResponse } from '../../core/services/events.service';
import { MetaService } from '../../core/services/meta.service';
import { WebAppConfigService } from '../../core/services/webapp-config.service';
import { Header } from '../../components/header/header';
import { Footer } from '../../components/footer/footer';
import { ScrollToTop } from '../../components/scroll-to-top/scroll-to-top';

interface CalendarDay {
  date: string;       // YYYY-MM-DD
  day: number;
  available: boolean;
  isToday: boolean;
  isPast: boolean;
  isCurrentMonth: boolean;
}

@Component({
  selector: 'app-actividad',
  standalone: true,
  imports: [CommonModule, Header, Footer, ScrollToTop],
  templateUrl: './actividad.html',
  styleUrl: './actividad.css',
})
export class ActividadPage implements OnInit, OnDestroy {
  event?: Event;
  subevento?: Subevento;
  isLoading = true;
  error = false;

  // Calendario
  calendarDays: CalendarDay[] = [];
  calendarWeekDays = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
  currentMonth: number;
  currentYear: number;
  availableFechas: string[] = [];
  isLoadingFechas = false;
  timezone = 'America/Guayaquil';

  private platformId = inject(PLATFORM_ID);
  private isBrowser: boolean;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private eventsService: EventsService,
    private metaService: MetaService,
    private webAppConfig: WebAppConfigService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    const now = new Date();
    this.currentMonth = now.getMonth();
    this.currentYear = now.getFullYear();
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      const urlSlug = params['url_slug'];
      const slugActividad = params['slug_actividad'];
      if (urlSlug && slugActividad) {
        this.loadData(urlSlug, slugActividad);
      }
    });
  }

  ngOnDestroy(): void {
    const config = this.webAppConfig.config();
    if (config) {
      this.metaService.resetToDefault(
        this.webAppConfig.getSiteName() + ' - ' + config.short_description,
        config.description,
        config.url_logo,
        config.url
      );
    }
  }

  private loadData(urlSlug: string, slugActividad: string): void {
    this.isLoading = true;
    this.error = false;

    this.eventsService.getEventBySlug(urlSlug).subscribe({
      next: (eventResponse) => {
        if (!eventResponse.success || !eventResponse.data) {
          this.error = true;
          this.isLoading = false;
          this.cdr.markForCheck();
          return;
        }
        this.event = eventResponse.data;

        this.eventsService.getSubeventoBySlug(this.event.eventoid, slugActividad).subscribe({
          next: (subResponse) => {
            if (!subResponse.success || !subResponse.data) {
              this.error = true;
              this.isLoading = false;
              this.cdr.markForCheck();
              return;
            }
            this.subevento = subResponse.data;
            this.isLoading = false;

            // Actualizar meta tags
            this.updateMeta();

            // Cargar fechas disponibles
            this.loadFechas();
            this.cdr.markForCheck();
          },
          error: () => {
            this.error = true;
            this.isLoading = false;
            this.cdr.markForCheck();
          }
        });
      },
      error: () => {
        this.error = true;
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  private loadFechas(): void {
    if (!this.event || !this.subevento) return;
    this.isLoadingFechas = true;

    this.eventsService.getSubeventoFechas(this.event.eventoid, this.subevento.subeventoid).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.availableFechas = response.data.fechas;
          this.timezone = response.data.timezone || 'America/Guayaquil';
        }
        this.isLoadingFechas = false;
        this.buildCalendar();
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoadingFechas = false;
        this.buildCalendar();
        this.cdr.markForCheck();
      }
    });
  }

  buildCalendar(): void {
    const today = this.getToday();
    const firstDay = new Date(this.currentYear, this.currentMonth, 1);
    const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0);
    const days: CalendarDay[] = [];

    // Días del mes anterior para completar la primera semana
    const startDow = (firstDay.getDay() + 6) % 7; // 0=Lun, 6=Dom
    for (let i = startDow - 1; i >= 0; i--) {
      const d = new Date(this.currentYear, this.currentMonth, -i);
      const dateStr = this.formatDateStr(d);
      days.push({
        date: dateStr,
        day: d.getDate(),
        available: false,
        isToday: dateStr === today,
        isPast: dateStr < today,
        isCurrentMonth: false,
      });
    }

    // Días del mes actual
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const date = new Date(this.currentYear, this.currentMonth, d);
      const dateStr = this.formatDateStr(date);
      days.push({
        date: dateStr,
        day: d,
        available: this.availableFechas.includes(dateStr),
        isToday: dateStr === today,
        isPast: dateStr < today,
        isCurrentMonth: true,
      });
    }

    // Días del mes siguiente para completar la última semana
    const endDow = (lastDay.getDay() + 6) % 7; // 0=Lun, 6=Dom
    for (let i = 1; i < 7 - endDow; i++) {
      const d = new Date(this.currentYear, this.currentMonth + 1, i);
      const dateStr = this.formatDateStr(d);
      days.push({
        date: dateStr,
        day: d.getDate(),
        available: false,
        isToday: false,
        isPast: dateStr < today,
        isCurrentMonth: false,
      });
    }

    this.calendarDays = days;
  }

  private getToday(): string {
    const now = new Date();
    return this.formatDateStr(now);
  }

  private formatDateStr(d: Date): string {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  prevMonth(): void {
    if (this.currentMonth === 0) {
      this.currentMonth = 11;
      this.currentYear--;
    } else {
      this.currentMonth--;
    }
    this.buildCalendar();
  }

  nextMonth(): void {
    if (this.currentMonth === 11) {
      this.currentMonth = 0;
      this.currentYear++;
    } else {
      this.currentMonth++;
    }
    this.buildCalendar();
  }

  get monthLabel(): string {
    return new Date(this.currentYear, this.currentMonth, 1).toLocaleDateString('es-ES', {
      month: 'long',
      year: 'numeric'
    });
  }

  selectDate(day: CalendarDay): void {
    if (!day.available || day.isPast || !this.event || !this.subevento) return;
    this.router.navigate(
      ['/evento', this.event.url_slug, 'actividad', this.subevento.slug, 'comprar'],
      { queryParams: { fecha: day.date } }
    );
  }

  goBack(): void {
    if (this.event) {
      this.router.navigate(['/evento', this.event.url_slug]);
    } else {
      this.router.navigate(['/']);
    }
  }

  getBannerImage(): string {
    if (this.subevento?.imagenes?.banner) return this.subevento.imagenes.banner;
    if (this.event?.imagenes?.banner) return this.event.imagenes.banner;
    return 'https://dummyimage.com/2000x576/cccccc/666666?text=Actividad';
  }
  getHorizontalImage(): string {
    if (this.subevento?.imagenes?.horizontal) return this.subevento.imagenes.horizontal;
    if (this.event?.imagenes?.horizontal) return this.event.imagenes.horizontal;
    return 'https://dummyimage.com/2000x576/cccccc/666666?text=Actividad';
  }

  private updateMeta(): void {
    if (!this.event || !this.subevento) return;
    const title = `${this.event.nombre} - ${this.subevento.nombre}`;
    const description = this.subevento.descripcion || `Reserva tu lugar en ${this.subevento.nombre} - ${this.event.nombre}`;
    const image = this.subevento.imagenes?.cuadrada || this.event.imagenes?.cuadrada || '';
    const baseUrl = this.webAppConfig.config()?.url || '';
    const url = `${baseUrl}/evento/${this.event.url_slug}/actividad/${this.subevento.slug}`;

    this.metaService.updateEventMeta({
      nombre: title,
      descripcion: description,
      imagenes: { cuadrada: image },
      url_slug: `${this.event.url_slug}/actividad/${this.subevento.slug}`
    }, baseUrl);
  }

  sanitizeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }
}
